/**
 * Zone List Component
 * List of zones with stats and actions
 */

import { Card, Badge, Button } from '../ui';
import { Map, Users, Package, Edit2, Trash2 } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { ZoneWithStats } from '../../types';
import { useCan } from '../../contexts/useAuth';

interface ZoneListProps {
  zones: ZoneWithStats[];
  selectedZoneId?: string;
  onSelectZone: (zoneId: string) => void;
  onEditZone: (zoneId: string) => void;
  onDeleteZone: (zoneId: string) => void;
  isLoading?: boolean;
}

export default function ZoneList({
  zones,
  selectedZoneId,
  onSelectZone,
  onEditZone,
  onDeleteZone,
  isLoading,
}: ZoneListProps) {
  const can = useCan();
  if (isLoading) {
    return (
      <div className="space-y-2">
        {[...Array(4)].map((_, i) => (
          <Card key={i} padding="sm">
            <div className="animate-pulse">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-4 h-4 bg-gray-200 rounded" />
                <div className="h-5 w-32 bg-gray-200 rounded" />
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded" />
                <div className="h-4 bg-gray-200 rounded" />
              </div>
            </div>
          </Card>
        ))}
      </div>
    );
  }

  if (zones.length === 0) {
    return (
      <Card>
        <div className="text-center py-8">
          <Map className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No zones</h3>
          <p className="text-gray-500 text-sm">
            Create your first zone by drawing on the map.
          </p>
        </div>
      </Card>
    );
  }

  return (
    <div className="space-y-2">
      {zones.map((zone) => {
        const isSelected = zone._id === selectedZoneId;

        return (
          <Card
            key={zone._id}
            padding="sm"
            className={cn(
              'cursor-pointer transition-all hover:shadow-md',
              isSelected && 'ring-2 ring-blue-500'
            )}
            onClick={() => onSelectZone(zone._id)}
          >
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center gap-2">
                <div
                  className="w-4 h-4 rounded"
                  style={{ backgroundColor: zone.color }}
                />
                <span className="font-medium text-gray-900">{zone.name}</span>
              </div>
              <Badge variant={zone.is_active ? 'success' : 'default'} size="sm">
                {zone.is_active ? 'Active' : 'Inactive'}
              </Badge>
            </div>

            {/* Stats */}
            {zone.stats && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Package className="h-3.5 w-3.5 text-gray-400" />
                  <span>{zone.stats.taskCount} tasks</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Users className="h-3.5 w-3.5 text-gray-400" />
                  <span>{zone.stats.driverCount} drivers</span>
                </div>
                <div className="flex items-center gap-1 text-sm text-gray-600">
                  <Map className="h-3.5 w-3.5 text-gray-400" />
                  <span>{zone.stats.activeRouteCount} routes</span>
                </div>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              {can('zones.manage') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditZone(zone._id);
                  }}
                  leftIcon={<Edit2 className="h-3.5 w-3.5" />}
                >
                  Edit
                </Button>
              )}
              {can('zones.manage') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDeleteZone(zone._id);
                  }}
                  leftIcon={<Trash2 className="h-3.5 w-3.5 text-red-500" />}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Delete
                </Button>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
}
